import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  EpicChildren,
  JiraProjectEpics,
  JiraProject,
  PickedProject,
} from '@/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface InsightsState {
  dateRange: string;
  clicked: boolean;
  isPowerMode: boolean;
  jiraProjectEpicChildren: EpicChildren | Record<string, never>;
  pickedProjects: PickedProject[];
  pickedProject: any;
  pickedEpic: any;
  selectedProject: any;
  selectedEpic: any;
  selectedChart: string;
  childrenLoading: boolean;
  jiraProjectEpics: JiraProjectEpics | Record<string, never>;
  jiraProjects: JiraProject[];
  insightActiveComponent: string;
  chatMode?: string;

  setDateRange: (dateRange: string) => void;
  setClicked: (clicked: boolean) => void;
  setIsPowerMode: (isPowerMode: boolean) => void;
  updateJiraProjects: (projects: JiraProject[]) => void;
  updateJiraProjectEpics: (epics: any) => void;
  updateJiraProjectEpicChildren: (epicChildren: any) => void;
  updateSelectedProject: (project: any) => void;
  updateSelectedEpic: (epic: any) => void;
  setPickedProject: (project: any) => void;
  setPickedEpic: (epic: any) => void;
  updateSelectedChart: (chart: string) => void;
  setChildrenLoading: (loading: boolean) => void;
  setInsightActiveComponent: (component: string) => void;
  setChatMode: (mode: string) => void;
  addPickedProject: (project: PickedProject) => void;
  removePickedProject: (project: PickedProject) => void;
  togglePickedProject: (project: PickedProject) => void;
  clearPickedProjects: () => void;
  setPickedProjects: (projects: PickedProject[]) => void;
  isProjectPicked: (project: PickedProject) => boolean;
}

const initialState = {
  dateRange: '',
  clicked: false,
  isPowerMode: false,
  jiraProjectEpicChildren: {} as EpicChildren | Record<string, never>,
  pickedProjects: [] as PickedProject[],
  pickedProject: '' as any,
  pickedEpic: null as any,
  selectedProject: '' as any,
  selectedEpic: '' as any,
  selectedChart: '',
  childrenLoading: false,
  jiraProjectEpics: {} as JiraProjectEpics | Record<string, never>,
  jiraProjects: [] as JiraProject[],
  insightActiveComponent: 'insights',
};

const useInsightsStore = create<InsightsState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setDateRange: (dateRange) =>
        set({
          dateRange: dateRange,
        }),
      setClicked: (clicked) =>
        set({
          clicked: clicked,
        }),
      setIsPowerMode: (isPowerMode) =>
        set({
          isPowerMode: isPowerMode,
        }),
      updateJiraProjects: (projects) =>
        set({
          jiraProjects: [...projects],
        }),

      updateJiraProjectEpics: (epics: any) => {
        set(() => ({
          jiraProjectEpics: {
            message: epics.message,
            count: epics.count,
            refreshTime: new Date(epics.refreshTime).toLocaleString(),
            issues: epics.issues,
          },
        }));
      },
      updateJiraProjectEpicChildren: (epicChildren: any) =>
        set(() => ({
          jiraProjectEpicChildren: {
            message: epicChildren.message,
            projectKey: epicChildren.projectKey,
            count: epicChildren.count,
            refreshTime: new Date(epicChildren.refreshTime).toLocaleString(),
            issues: epicChildren.issues,
            completionStatus: epicChildren.completionStatus,
            progressPercentage: epicChildren.progressPercentage,
          },
        })),
      updateSelectedProject: (project) =>
        set({
          selectedProject: project,
        }),
      updateSelectedEpic: (epic) =>
        set({
          selectedEpic: epic,
        }),
      setPickedProject: (project) =>
        set({
          pickedProject: project,
        }),
      setPickedEpic: (epic) =>
        set({
          pickedEpic: epic,
        }),
      updateSelectedChart: (chart) =>
        set({
          selectedChart: chart,
        }),
      setChildrenLoading: (loading) =>
        set({
          childrenLoading: loading,
        }),

      setInsightActiveComponent: (component) =>
        set({
          insightActiveComponent: component,
        }),
      setChatMode: (mode) =>
        set({
          chatMode: mode,
        }),

      // New methods for managing pickedProjects array
      addPickedProject: (project) =>
        set((state) => {
          const exists = state.pickedProjects.find((p) => p.id === project.id);
          if (exists) return state;

          return {
            pickedProjects: [
              ...state.pickedProjects,
              { ...project, checked: true },
            ],
          };
        }),

      removePickedProject: (project) =>
        set((state) => ({
          pickedProjects: state.pickedProjects.filter(
            (p) => p.id !== project.id
          ),
        })),

      togglePickedProject: (project) =>
        set((state) => {
          const existingIndex = state.pickedProjects.findIndex(
            (p) => p.id === project.id
          );

          if (existingIndex >= 0) {
            return {
              pickedProjects: state.pickedProjects.filter(
                (p) => p.id !== project.id
              ),
            };
          } else {
            return {
              pickedProjects: [
                ...state.pickedProjects,
                { ...project, checked: true },
              ],
            };
          }
        }),

      clearPickedProjects: () =>
        set({
          pickedProjects: [],
        }),

      setPickedProjects: (projects) =>
        set({
          pickedProjects: projects.map((p) => ({ ...p, checked: true })),
        }),

      isProjectPicked: (project) => {
        const state = get();
        return state.pickedProjects.some(
          (p) => p.id === project.id && p.checked === true
        );
      },
    }),
    {
      name: 'InsightsStore',
    }
  )
);

export default useInsightsStore;
